import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useSession } from '../context/SessionContext';
import FeedbackButton from './FeedbackButton';
import api from '../utils/api';

const Layout = ({ 
  children, 
  showHeader = true, 
  showFooter = true,
  title,
  backLink,
  actions,
  setShowShareModal,  // Add this prop to allow opening the ShareLinksModal
  sessionOverride,    // Allow components to override session data
  fullWidth = false   // Allow full-width layout without container
}) => {
  const { session: contextSession, leaveSession, socket } = useSession();
  // Use sessionOverride if provided, otherwise use the session from context
  const session = sessionOverride || contextSession;
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sessionName, setSessionName] = useState(session?.name || '');
  const [isEditingSessionName, setIsEditingSessionName] = useState(false);
  const [tempSessionName, setTempSessionName] = useState('');

  // Update sessionName when session changes
  useEffect(() => {
    if (session?.name) {
      setSessionName(session.name);
    }
  }, [session]);

  // Listen for session name updates via socket
  useEffect(() => {
    if (socket && session?.id) {
      const handleSessionNameUpdate = (data) => {
        if (data.sessionId === session.id) {
          setSessionName(data.name);
        }
      };

      socket.on('session-name-updated', handleSessionNameUpdate);

      return () => {
        socket.off('session-name-updated', handleSessionNameUpdate);
      };
    }
  }, [socket, session]);

  // Function to handle section scrolling when page loads with hash
  useEffect(() => {
    // Check if URL has a hash and we're on the home page
    if (window.location.hash && location.pathname === '/') {
      const id = window.location.hash.replace('#', '');
      const element = document.getElementById(id);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location.pathname]);

  const handleLogout = () => {
    leaveSession();
    navigate('/');
  };

  const handleStartEditSessionName = () => {
    setTempSessionName(sessionName);
    setIsEditingSessionName(true);
  };

  const handleCancelEditSessionName = () => {
    setIsEditingSessionName(false);
    setTempSessionName('');
  };

  const handleSaveSessionName = async () => {
    if (!tempSessionName.trim()) {
      alert('Session name cannot be empty');
      return;
    }

    try {
      const response = await api.put(`/api/sessions/${session.id}`, {
        name: tempSessionName.trim()
      });

      if (response.data.session) {
        setSessionName(response.data.session.name);
        setIsEditingSessionName(false);
        setTempSessionName('');
        
        // Update localStorage if needed
        localStorage.setItem('sessionName', response.data.session.name);
      }
    } catch (error) {
      console.error('Error updating session name:', error);
      alert('Failed to update session name. Please try again.');
    }
  };

  const handleSessionNameKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveSessionName();
    } else if (e.key === 'Escape') {
      handleCancelEditSessionName();
    }
  };

  // Get the segments of the URL path for more accurate detection
  const pathSegments = location.pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments.length > 0 ? pathSegments[pathSegments.length - 1] : '';
  const secondLastSegment = pathSegments.length > 1 ? pathSegments[pathSegments.length - 2] : '';
  
  // Check if we're on the QuestionManager page
  const isQuestionManager = lastSegment === 'manage' || location.pathname.endsWith('/manage');
  
  // Check if we're on the TemplateEditor page
  const isTemplateEditor = 
    lastSegment === 'templates' || 
    secondLastSegment === 'templates' || 
    location.pathname.includes('/templates') || 
    location.pathname.includes('/template');
  
  // Clean up debugging logs now that everything is working correctly

  const hideFeedback = location.pathname.includes('/display') || location.pathname.includes('/audience') || location.pathname.startsWith('/view/') || location.pathname.startsWith('/flow/');

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      {showHeader && (
        <header className="bg-white shadow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Link to="/" className="flex items-center">
                  <img
                    src="/asktide_logo.png"
                    alt="AskTide Logo"
                    className="h-10 sm:h-12 md:h-14 w-auto"
                  />
                </Link>
              </div>
              
              {/* Mobile menu button */}
              <div className="md:hidden flex items-center">
                {session && session.id && (
                  <div className="flex items-center space-x-2 mr-4">
                    {/* Display the most relevant actions for Question Manager */}
                    {isQuestionManager && (
                      <>
                        <Link 
                          to={`/session/${session.id}/templates`}
                          className="text-xs px-2 py-1 border border-primary-500 rounded-md text-primary-600 bg-white"
                        >
                          Templates
                        </Link>
                        <Link 
                          to={`/session/${session.id}/display`}
                          className="text-xs px-2 py-1 border border-primary-500 rounded-md text-primary-600 bg-white"
                        >
                          Display
                        </Link>
                      </>
                    )}
                    
                    {/* Display relevant actions for Template Editor */}
                    {isTemplateEditor && (
                      <>
                        <Link 
                          to={`/session/${session.id}/manage`}
                          className="text-xs px-2 py-1 border border-primary-500 rounded-md text-primary-600 bg-white"
                        >
                          Questions
                        </Link>
                      </>
                    )}
                    
                    {/* For other pages */}
                    {!isQuestionManager && !isTemplateEditor && session.id && (
                      <Link 
                        to={`/session/${session.id}/manage`}
                        className="text-xs px-2 py-1 border border-primary-500 rounded-md text-primary-600 bg-white"
                      >
                        Manage
                      </Link>
                    )}
                  </div>
                )}
                
                <button
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-gray-500 hover:text-gray-600 focus:outline-none"
                >
                  {mobileMenuOpen ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                    </svg>
                  )}
                </button>
              </div>
              
              {/* Desktop menu */}
              <div className="hidden md:flex items-center space-x-4">
                {!session && !isQuestionManager && !isTemplateEditor && (
                  <nav className="flex items-center space-x-4">
                    <a 
                      href="/#features" 
                      onClick={(e) => {
                        if (location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                      data-umami-event="Nav - Features"
                    >
                      Features
                    </a>
                    <a 
                      href="/#pricing" 
                      onClick={(e) => {
                        if (location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                      data-umami-event="Nav - Pricing"
                    >
                      Pricing
                    </a>
                    <a 
                      href="/#about" 
                      onClick={(e) => {
                        if (location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                      data-umami-event="Nav - About"
                    >
                      About
                    </a>
                    <Link 
                      to="/free-rundown-creator"
                      className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium"
                      data-umami-event="Nav - Free Rundown Creator"
                    >
                      Free Rundown Creator
                    </Link>
                    <Link 
                      to="/create" 
                      className="ml-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      data-umami-event="Create Session - Header"
                    >
                      Create Session
                    </Link>
                  </nav>
                )}
                {session && (
                  <>
                    <div className="flex items-center bg-gray-100 px-3 py-1.5 rounded-md mr-3 border border-gray-200 group">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500 mr-1.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                      </svg>
                      {isEditingSessionName ? (
                        <div className="flex items-center space-x-2">
                          <input
                            type="text"
                            value={tempSessionName}
                            onChange={(e) => setTempSessionName(e.target.value)}
                            onKeyDown={handleSessionNameKeyPress}
                            className="text-sm px-2 py-0.5 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                            autoFocus
                            placeholder="Session name"
                          />
                          <button
                            onClick={handleSaveSessionName}
                            className="p-1 text-green-600 hover:text-green-700"
                            title="Save"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            onClick={handleCancelEditSessionName}
                            className="p-1 text-red-600 hover:text-red-700"
                            title="Cancel"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-600 flex items-center">
                          Session: <span className="font-medium text-gray-800 ml-1">{sessionName}</span>
                          <button
                            onClick={handleStartEditSessionName}
                            className="ml-2 p-1 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Edit session name"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </span>
                      )}
                    </div>
                    
                    {/* Remove automatic navigation for Question Manager since it's provided via actions prop */}
                    
                    {/* We've removed automatic navigation for Template Editor as it will be provided via components */}
                    
                    {/* Standard session navigation for other pages */}
                    {session.id && !isQuestionManager && !isTemplateEditor && (
                      <Link 
                        to={`/session/${session.id}/manage`}
                        className="inline-flex items-center px-3 py-1.5 border border-primary-500 text-sm font-medium rounded-md text-primary-600 bg-white hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Manage Questions
                      </Link>
                    )}
                    
                    <button
                      onClick={handleLogout}
                      className="ml-4 inline-flex items-center px-3 py-1.5 border border-red-300 text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-400 transition-colors"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-1 1h-1a1 1 0 01-1-1V4a1 1 0 00-1-1H5a1 1 0 00-1 1v12a1 1 0 001 1h7a1 1 0 001-1v-1a1 1 0 011-1h1a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V3z" clipRule="evenodd" />
                        <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L13.586 12H8a1 1 0 110-2h5.586l-2.293-2.293a1 1 0 111.414-1.414l4 4z" clipRule="evenodd" />
                      </svg>
                      Leave Session
                    </button>
                  </>
                )}
                {actions}
              </div>
            </div>
            
            {/* Mobile menu, show/hide based on menu state */}
            {mobileMenuOpen && (
              <div className="md:hidden pt-2 pb-3 border-t border-gray-200 mt-4">
                {!session ? (
                  <div className="flex flex-col space-y-2">
                    <a 
                      href="/#features" 
                      onClick={(e) => {
                        setMobileMenuOpen(false);
                        if (location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium"
                    >
                      Features
                    </a>
                    <a 
                      href="/#pricing" 
                      onClick={(e) => {
                        setMobileMenuOpen(false);
                        if (location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium"
                    >
                      Pricing
                    </a>
                    <a 
                      href="/#about" 
                      onClick={(e) => {
                        setMobileMenuOpen(false);
                        if (location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium"
                    >
                      About
                    </a>
                    <Link 
                      to="/free-rundown-creator"
                      onClick={() => setMobileMenuOpen(false)}
                      className="text-gray-600 hover:text-gray-900 px-3 py-2 text-base font-medium"
                    >
                      Free Rundown Creator
                    </Link>
                    <Link 
                      to="/create" 
                      onClick={() => setMobileMenuOpen(false)}
                      className="w-full text-center mt-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      Create Session
                    </Link>
                  </div>
                ) : (
                  <div className="flex flex-col space-y-4">
                    <div className="px-3 py-2 bg-gray-100 mx-3 rounded-md border border-gray-200 mb-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1 min-w-0">
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-500 mr-1.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
                          </svg>
                          {isEditingSessionName ? (
                            <div className="flex items-center space-x-1 flex-1">
                              <input
                                type="text"
                                value={tempSessionName}
                                onChange={(e) => setTempSessionName(e.target.value)}
                                onKeyDown={handleSessionNameKeyPress}
                                className="text-sm px-2 py-0.5 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white flex-1 min-w-0"
                                autoFocus
                                placeholder="Session name"
                              />
                              <button
                                onClick={handleSaveSessionName}
                                className="p-1 text-green-600 hover:text-green-700 flex-shrink-0"
                                title="Save"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                              </button>
                              <button
                                onClick={handleCancelEditSessionName}
                                className="p-1 text-red-600 hover:text-red-700 flex-shrink-0"
                                title="Cancel"
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                              </button>
                            </div>
                          ) : (
                            <div className="text-sm text-gray-600 flex items-center flex-1 min-w-0">
                              Session: <span className="font-medium text-gray-800 ml-1 truncate">{sessionName}</span>
                              <button
                                onClick={handleStartEditSessionName}
                                className="ml-2 p-1 text-gray-400 hover:text-gray-600 flex-shrink-0"
                                title="Edit session name"
                              >
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                </svg>
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Show different menu options based on current page */}
                    {isQuestionManager ? (
                      <div className="space-y-1">
                        <Link 
                          to={`/session/${session.id}/templates`}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-3 py-2 text-primary-600 font-medium hover:bg-gray-50"
                        >
                          Manage Templates
                        </Link>
                        <Link 
                          to={`/session/${session.id}/display`}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-3 py-2 text-primary-600 font-medium hover:bg-gray-50"
                        >
                          Display View
                        </Link>
                        <button
                          onClick={() => {
                            setMobileMenuOpen(false);
                            if (setShowShareModal) {
                              setShowShareModal(true);
                            }
                          }}
                          className="block w-full text-left px-3 py-2 text-primary-600 font-medium hover:bg-gray-50"
                        >
                          Share Links
                        </button>
                      </div>
                    ) : isTemplateEditor ? (
                      <div className="space-y-1">
                        <Link 
                          to={`/session/${session.id}/manage`}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-3 py-2 text-primary-600 font-medium hover:bg-gray-50"
                        >
                          Back to Questions
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-1">
                        {session.id && (
                          <Link 
                            to={`/session/${session.id}/manage`}
                            onClick={() => setMobileMenuOpen(false)}
                            className="block px-3 py-2 text-primary-600 font-medium hover:bg-gray-50"
                          >
                            Manage Questions
                          </Link>
                        )}
                        <Link 
                          to={`/session/${session.id}/display`}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-3 py-2 text-primary-600 font-medium hover:bg-gray-50"
                        >
                          Display View
                        </Link>
                        <Link 
                          to={`/session/${session.id}/audience`}
                          onClick={() => setMobileMenuOpen(false)}
                          className="block px-3 py-2 text-primary-600 font-medium hover:bg-gray-50"
                        >
                          Audience View
                        </Link>
                      </div>
                    )}
                    
                    <div className="pt-2 border-t border-gray-200">
                      <button
                        onClick={() => {
                          setMobileMenuOpen(false);
                          handleLogout();
                        }}
                        className="flex items-center w-full text-left px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 font-medium transition-colors"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-1 1h-1a1 1 0 01-1-1V4a1 1 0 00-1-1H5a1 1 0 00-1 1v12a1 1 0 001 1h7a1 1 0 001-1v-1a1 1 0 011-1h1a1 1 0 011 1v3a1 1 0 01-1 1H4a1 1 0 01-1-1V3z" clipRule="evenodd" />
                          <path fillRule="evenodd" d="M16.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L13.586 12H8a1 1 0 110-2h5.586l-2.293-2.293a1 1 0 111.414-1.414l4 4z" clipRule="evenodd" />
                        </svg>
                        Leave Session
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </header>
      )}

      <main className="flex-grow">
        {fullWidth ? (
          children
        ) : (
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {children}
          </div>
        )}
      </main>

      {showFooter && (
        <footer className="bg-gray-900 border-t border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8">
              {/* Logo and Description */}
              <div className="lg:col-span-5">
                <Link to="/" className="inline-block mb-4">
                  <img
                    src="/asktide_logo.png"
                    alt="AskTide Logo"
                    className="h-10 w-auto"
                  />
                </Link>
                <p className="text-gray-400 text-sm leading-relaxed max-w-sm">
                  Engage your audience with live Q&A and interactive question management. 
                  Perfect for broadcasts, webinars, conferences, and live events with real-time audience participation.
                </p>
              </div>

              {/* Product Links */}
              <div className="lg:col-span-3">
                <h3 className="text-white font-semibold mb-4 text-sm">Product</h3>
                <ul className="space-y-3">
                  <li>
                    <a 
                      href="/#features"
                      onClick={(e) => {
                        if (location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                      data-umami-event="Footer - Features"
                    >
                      Features
                    </a>
                  </li>
                  <li>
                    <a 
                      href="/#pricing"
                      onClick={(e) => {
                        if (location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                      data-umami-event="Footer - Pricing"
                    >
                      Pricing
                    </a>
                  </li>
                  <li>
                    <Link 
                      to="/free-rundown-creator"
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                      data-umami-event="Footer - Free Rundown Creator"
                    >
                      Free Rundown Creator
                    </Link>
                  </li>
                  <li>
                    <a 
                      href="/#about"
                      onClick={(e) => {
                        if (location.pathname === '/') {
                          e.preventDefault();
                          document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
                        }
                      }}
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                      data-umami-event="Footer - About"
                    >
                      About
                    </a>
                  </li>
                </ul>
              </div>

              {/* Support Links */}
              <div className="lg:col-span-4">
                <h3 className="text-white font-semibold mb-4 text-sm">Support</h3>
                <ul className="space-y-3">
                  <li>
                    <a 
                      href="mailto:support@asktide.com"
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                      data-umami-event="Footer - Contact Us"
                    >
                      Contact Us
                    </a>
                  </li>
                  <li>
                    <Link 
                      to="/privacy-policy"
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                      data-umami-event="Footer - Privacy Policy"
                    >
                      Privacy Policy
                    </Link>
                  </li>
                  <li>
                    <Link 
                      to="/terms-of-service"
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                      data-umami-event="Footer - Terms of Service"
                    >
                      Terms of Service
                    </Link>
                  </li>
                </ul>
              </div>
            </div>

            {/* Copyright */}
            <div className="mt-12 pt-8 border-t border-gray-800">
              <p className="text-center text-sm text-gray-500">
                &copy; {new Date().getFullYear()} AskTide. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      )}

      {!hideFeedback && <FeedbackButton />}
    </div>
  );
};

export default Layout; 